/**
 * FloatField & FloatWrapper — reusable floating/notched-label form primitives
 *
 * FloatField   — self-contained field for text/email/tel/textarea/select/date
 * FloatWrapper — label-on-border wrapper for custom children (DatePicker, Switch, etc.)
 *
 * The "notch" technique:
 *   • Wrapper has `mt-3` so the label has room to poke above the input.
 *   • Label is absolute at `top-0 -translate-y-1/2` — its vertical center sits
 *     exactly ON the input's top border line.
 *   • `bg-white dark:bg-card` on the label masks the border behind the text.
 */

import { cn } from "@/lib/utils";

// ── FloatField ─────────────────────────────────────────────────────────────────

export interface FloatFieldProps {
  label: string;
  type?: "text" | "email" | "tel" | "textarea" | "select" | "date" | "number";
  placeholder?: string;
  required?: boolean;
  options?: string[];   // for type="select"
  value: string;
  error?: string;
  onChange: (value: string) => void;
  className?: string;   // applied to the outer wrapper div
  inputClassName?: string;
}

export function FloatField({
  label,
  type = "text",
  placeholder,
  required,
  options,
  value,
  error,
  onChange,
  className,
  inputClassName,
}: FloatFieldProps) {
  const borderCls = error ? "border-destructive" : "border-[#d0d5e8]";
  const focusCls  = "focus:border-indigo-500 focus:outline-none";

  const base = cn(
    "w-full rounded-xl border bg-white dark:bg-card px-4 text-sm text-foreground",
    "transition-colors placeholder:text-muted-foreground/40",
    borderCls,
    focusCls,
    inputClassName,
  );

  return (
    <div className={cn("relative mt-3 flex-1", className)}>
      <label
        className={cn(
          "pointer-events-none absolute left-4 top-0 -translate-y-1/2 z-10",
          "bg-white dark:bg-card px-1.5 text-[13px] font-bold leading-none tracking-tight",
          error ? "text-destructive" : "text-foreground",
        )}
      >
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {type === "textarea" ? (
        <textarea
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(base, "min-h-[110px] resize-none py-4")}
        />
      ) : type === "select" ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(base, "h-14 appearance-none cursor-pointer")}
        >
          <option value="">{placeholder ?? "Select…"}</option>
          {options?.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(base, "h-14")}
        />
      )}

      {error && (
        <p className="mt-1 text-[11px] text-destructive">{error}</p>
      )}
    </div>
  );
}

// ── FloatWrapper ───────────────────────────────────────────────────────────────

export interface FloatWrapperProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Wrap any custom control (DatePicker, Select from Radix, file input, etc.)
 * to give it the floating/notched label treatment.
 *
 * The child component should be styled with:
 *   className="w-full rounded-xl border border-[#d0d5e8] h-14 bg-white dark:bg-card"
 */
export function FloatWrapper({
  label,
  required,
  error,
  children,
  className,
}: FloatWrapperProps) {
  return (
    <div className={cn("relative mt-3 flex-1 [&_button:focus]:ring-0 [&_button:focus]:ring-offset-0 [&_button:focus-visible]:ring-0 [&_button:focus-visible]:ring-offset-0", className)}>
      <label
        className={cn(
          "pointer-events-none absolute left-4 top-0 -translate-y-1/2 z-10",
          "bg-white dark:bg-card px-1.5 text-[13px] font-bold leading-none tracking-tight",
          error ? "text-destructive" : "text-foreground",
        )}
      >
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[11px] text-destructive">{error}</p>
      )}
    </div>
  );
}

// ── Shared className helper for child controls inside FloatWrapper ─────────────

/** Apply to the trigger/input of a custom control placed inside FloatWrapper */
// eslint-disable-next-line react-refresh/only-export-components
export const floatChildCls = cn(
  "w-full rounded-xl border border-[#d0d5e8] h-14 bg-white dark:bg-card",
  "px-4 text-sm text-foreground",
  "focus:border-indigo-500 focus:outline-none focus:ring-0 focus:ring-offset-0 transition-colors",
);
